import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Injector, Injectable } from '@angular/core';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { ESCAPE } from '@angular/cdk/keycodes';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

import {
  SgAsyncGuiPlugin,
  SgAsyncGuiPluginContext,
  SgAsyncGuiPlugins,
  SgAsyncGuiScrollStrategies,
  SgAsyncGuiPhase,
  SgAsyncGuiPhaseType,
  SgAsyncGuiEvent,
  SgAsyncGuiEventType,
} from '../../../../types';

import { SgAsyncGuiRef } from '../../../../async-gui-ref';
import { SgAsyncGuiDialogContainerContext, SgAsyncGuiDialogContainer } from './container.component';

declare module '../../../../types' {
  interface SgAsyncGuiPlugins {
    dialog: SgAsyncGuiDialogPlugin;
  }
  interface SgAsyncGuiOptions {
    backdropClass?: string;
    hasBackdrop?: boolean;
    autoFocus?: boolean;
    closeOnNavigation?: boolean;
    scrollStrategy?: SgAsyncGuiScrollStrategies;
    disableClose?: boolean;
  }
}

@Injectable()
export class SgAsyncGuiDialogPlugin implements SgAsyncGuiPlugin {
  readonly name = 'dialog' as 'dialog';

  constructor(private overlay: Overlay, private dialog: MatDialog) {}

  open(promise: Promise<any>, ctx: SgAsyncGuiPluginContext): SgAsyncGuiRef {
    let dialogRef: MatDialogRef<any>;
    const events = new Subject<SgAsyncGuiEvent | SgAsyncGuiPhase>();
    const sgAsyncGuiRef = SgAsyncGuiRef.create(this.name, ctx, events.asObservable());
    const injector = Injector.create({
      providers: [{ provide: SgAsyncGuiRef, useValue: sgAsyncGuiRef }],
      parent: ctx.injector,
    });

    const terminate = (value?: any, emitForceClosing?: boolean) => {
      if (emitForceClosing) {
        events.next({ name: 'event', type: 'forceClosing' });
      }
      events.complete();
      if (dialogRef) {
        dialogRef.close();
      }
    };
    sgAsyncGuiRef.closed.subscribe(terminate); // no unsub, ref container will complete.

    const config: MatDialogConfig<SgAsyncGuiDialogContainerContext> = { data: { component: ctx.component, injector } };
    if (ctx.options) {
      ['backdropClass', 'autoFocus', 'closeOnNavigation'].forEach((k) => {
        if (ctx.options.hasOwnProperty(k)) {
          config[k] = ctx.options[k];
        }
      });
      if (ctx.options.hasOwnProperty('scrollStrategy')) {
        config.scrollStrategy = this.getScrollStrategyInstance(ctx.options.scrollStrategy);
      }

      config.panelClass = 'sg-async-gui-dialog-container';

      // we want manual control over click/keyboard events.
      config.hasBackdrop = true;
      config.disableClose = true;
    }

    const phases = ctx.options.applyOn.reduce((o, p) => {
      o[p] = true;
      return o;
    }, {} as Record<SgAsyncGuiPhaseType, true>);
    const emitPhase = (type: SgAsyncGuiPhaseType) => events.next({ name: 'phase', type });
    const createDialogRef = () => {
      dialogRef = this.dialog.open(SgAsyncGuiDialogContainer, config);

      // TODO: merge the streams.
      dialogRef
        .keydownEvents() // no unsub, ref container will complete.
        .pipe(filter((event) => event.keyCode === ESCAPE))
        .subscribe(() => events.next({ name: 'event', type: 'userCloseRequested' })),
        dialogRef
          .backdropClick() // no unsub, ref container will complete.
          .subscribe(() => events.next({ name: 'event', type: 'userCloseRequested' }));
    };

    if (phases.run) {
      createDialogRef();
      dialogRef.afterOpened().subscribe(() => {
        emitPhase('run');
        promise
          .then(() => (phases.resolve ? emitPhase('resolve') : terminate(undefined, true)))
          .catch((err) => (phases.reject ? emitPhase('reject') : terminate(undefined, true)));
      });
    } else {
      const lazyDialogRef = (value?: any, error?: Error) => {
        createDialogRef();
        dialogRef.afterOpened().subscribe(() => (error ? emitPhase('reject') : emitPhase('resolve')));
      };
      promise
        .then((value) => (phases.resolve ? lazyDialogRef(value) : terminate(undefined, true)))
        .catch((err) => (phases.reject ? lazyDialogRef(null, err) : terminate(undefined, true)));
    }

    return sgAsyncGuiRef;
  }

  private getScrollStrategyInstance(strategy: SgAsyncGuiScrollStrategies): ScrollStrategy {
    switch (strategy) {
      case 'block':
        return this.overlay.scrollStrategies.block();
      case 'reposition':
        return this.overlay.scrollStrategies.reposition();
      case 'close':
        return this.overlay.scrollStrategies.close();
      case 'noop':
      default:
        return this.overlay.scrollStrategies.noop();
    }
  }
}
